import axios from "axios";
import React, { useState, useEffect } from "react";
import { BASE_URL } from "../../../env";
import Sidebar from "../../../components/layout/Sidebar";
import { Form, Row, Col } from "react-bootstrap";
import Select from "react-select";
import CustomPagination from "../../../components/ui/CustomPagination";
import DataTable from "../../../components/ui/DataTable";
import { getDisbursementAmount, formatAmount } from "../../../utils/utils";
import { useAdminAuth } from "../../auth/AdminAuthProvider";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import { MdExpandMore } from "react-icons/md";
import StandardizedDateInput from "../../../components/ui/StandardizedDateInput";

const ProcessingFeeSummaryReport = () => {
  const { adminUser } = useAdminAuth();
  const [reportData, setReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);
  const [currentPage, setCurrentPage] = useState(1);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedBranchFilter, setSelectedBranchFilter] = useState(null); // State for branch name filter
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState(null); // State for employee name filter
  const [searchTerm, setSearchTerm] = useState("");

  const itemsPerPage = 20;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const paginationData = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const OpenSidebar = () => {
    setOpenSidebarToggle(!openSidebarToggle);
  };

  useEffect(() => {
    const getReportData = async (id) => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/getProcessingFeeSummaryReportData`,
          {
            params: { id },
          }
        );
        console.log("Received data for Business Loan processing fee summary report:", response.data);
        console.log("Total records received:", response.data.length);
        if (response.data) {
          setReportData(response.data);
        } else {
          console.log("Error in fetching Data");
        }
      } catch (error) {
        console.log("Error fetching Data:", error);
      }
    };

    getReportData(adminUser.user.id);
  }, [adminUser.user.id]);

  // Extract unique branch names and employee names for dropdown options
  const branchOptions = [...new Set(reportData.map((data) => data?.branchName))]
    .filter((name) => name)
    .map((name) => ({ value: name, label: name }));

  const employeeOptions = [...new Set(reportData.map((data) => data?.username))]
    .filter((name) => name)
    .map((name) => ({ value: name, label: name }));

  // Automatically filter data when fromDate, toDate, or searchTerm changes
  useEffect(() => {
    let filtered = reportData;

    if (fromDate || toDate) {
      filtered = filtered.filter((item) => {
        // Standardize date filter to include the full end day (matching Outstanding Report)
        if (!item.branchManagerStatusUpdatedAt) return false;

        const updatedAt = new Date(item.branchManagerStatusUpdatedAt);
        const start = fromDate ? new Date(fromDate) : new Date(0);
        start.setHours(0, 0, 0, 0);
        const end = toDate ? new Date(toDate) : new Date();
        end.setHours(23, 59, 59, 999);

        return updatedAt >= start && updatedAt <= end;
      });
    }

    if (selectedBranchFilter?.value) {
      filtered = filtered.filter(
        (item) => item.branchName === selectedBranchFilter.value
      );
    }

    if (selectedEmployeeFilter?.value) {
      filtered = filtered.filter(
        (item) => item.username === selectedEmployeeFilter.value
      );
    }

    if (searchTerm && searchTerm.trim() !== "") {
      const term = searchTerm.trim().toLowerCase();
      filtered = filtered.filter((item) => {
        const branchName = String(item?.branchName || "").toLowerCase();
        const regionName = String(item?.regionName || "").toLowerCase();
        const divisionName = String(item?.divisionName || "").toLowerCase();
        const memberName = String(item?.memberName || "").toLowerCase();
        const customerId = String(item?.customerId || "").toLowerCase();
        const loanId = String(item?.loanId || "").toLowerCase();
        const phoneNumber = String(item?.phoneNumber || "").toLowerCase();

        return (
          branchName.includes(term) ||
          regionName.includes(term) ||
          divisionName.includes(term) ||
          memberName.includes(term) ||
          customerId.includes(term) ||
          loanId.includes(term) ||
          phoneNumber.includes(term)
        );
      });
    }

    // Helpers to normalize amounts for consistency with ProcessingChargeReport
    const asIntByLoanType = (amount, loanType) =>
      loanType === "Business Loan" ? Math.floor(Number(amount) || 0) : Math.round(Number(amount) || 0);

    // Group by branch after filtering - include ALL members with processingCharge for correct totals
    const groupedByBranch = filtered.reduce((acc, item) => {
      const key = item.branchName;
      if (!acc[key]) {
        acc[key] = {
          branchName: item.branchName,
          regionName: item.regionName,
          divisionName: item.divisionName,
          members: [],
        };
      }
      // Include all records that have processing fees
      const hasProcessingFee = Number(item.processingCharge) > 0;
      const hasTransactionAmount = Number(item.transactionAmount) > 0;

      if (hasProcessingFee || hasTransactionAmount) {
        acc[key].members.push(item);
      }
      return acc;
    }, {});

    // Rest of the summarization logic with consistent rounding/integer conversion
    const summarizedData = Object.values(groupedByBranch).map((branch) => {
      const numberOfAccounts = branch.members.length;
      const totalDisbursedAmount = branch.members.reduce((sum, member) => {
        return sum + getDisbursementAmount(member);
      }, 0);

      const totalRateOfInterest = branch.members.reduce((sum, member) => {
        return (
          sum + (Number(member.proposedLoanDetails?.rateOfInterest) || 0)
        );
      }, 0);

      const totalProcessingFee = branch.members.reduce((sum, member) => {
        return sum + asIntByLoanType(member.processingCharge, member.loanType);
      }, 0);

      const totalGST = branch.members.reduce((sum, member) => {
        return sum + asIntByLoanType(member.gst, member.loanType);
      }, 0);

      return {
        ...branch,
        numberOfAccounts,
        totalDisbursedAmount,
        averageLoanSize: numberOfAccounts
          ? totalDisbursedAmount / numberOfAccounts
          : 0,
        averageRateOfInterest: numberOfAccounts
          ? totalRateOfInterest / numberOfAccounts
          : 0,
        totalProcessingFee,
        totalSGST: Math.floor(totalGST / 2),
        totalCGST: totalGST - Math.floor(totalGST / 2),
        totalGST: totalGST,
      };
    });

    setFilteredData(summarizedData);
    setCurrentPage(1);
  }, [
    fromDate,
    toDate,
    reportData,
    selectedBranchFilter,
    selectedEmployeeFilter,
    searchTerm,
  ]); // Added selectedEmployeeFilter

  // Define columns with label and render logic
  const columns = [
    { label: "S No", render: (data, index) => index + 1 },
    { label: "State", render: (data) => "Tamilnadu" },
    { label: "Region", render: (data) => data.regionName || "N/A" },
    { label: "Division", render: (data) => data.divisionName || "N/A" },
    { label: "Branch Name", render: (data) => data.branchName },
    { label: "No. of Accounts", render: (data) => data.numberOfAccounts },
    {
      label: "Disbursement Amount",
      render: (data) => formatAmount(data.totalDisbursedAmount),
    },
    {
      label: "Average Loan Size",
      render: (data) => formatAmount(data.averageLoanSize),
    },
    {
      label: "Avg Interest Rate",
      render: (data) => formatAmount(data.averageRateOfInterest),
    },
    {
      label: "Processing Fee",
      render: (data) => formatAmount(data.totalProcessingFee),
    },
    {
      label: "SGST (9%)",
      render: (data) => formatAmount(data.totalSGST),
    },
    {
      label: "CGST (9%)",
      render: (data) => formatAmount(data.totalCGST),
    },
    {
      label: "Total GST",
      render: (data) => formatAmount(data.totalGST),
    },
  ];

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          <div className="master-search-section">
            <h1 className="TableHeaderText w-100 text-start">Processing Fee Summary Report</h1>

            <button
              className={`filter-toggle-btn ${showMobileFilters ? "active" : ""}`}
              onClick={() => setShowMobileFilters(!showMobileFilters)}
            >
              <span style={{ fontWeight: 600, fontSize: "14px" }}>
                {showMobileFilters ? "Hide Filters" : "Show Filters"}
              </span>
              <MdExpandMore size={22} />
            </button>

            <div className={`filter-container ${!showMobileFilters ? "mobile-hidden" : "mobile-visible"}`}>
              <Row>
                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>Search Customer</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search Name, Customer ID, Loan Acc No, Phone..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={2} md={6}>
                  <Form.Group>
                    <Form.Label>From Date</Form.Label>
                    <StandardizedDateInput
                      value={fromDate}
                      onChange={(event) => {
                        const val = event.target.value;
                        setFromDate(val);
                        if (val && toDate && selectedBranchFilter && selectedEmployeeFilter) {
                          setTimeout(() => setShowMobileFilters(false), 300);
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={2} md={6}>
                  <Form.Group>
                    <Form.Label>To Date</Form.Label>
                    <StandardizedDateInput
                      value={toDate}
                      onChange={(event) => {
                        const val = event.target.value;
                        setToDate(val);
                        if (fromDate && val && selectedBranchFilter && selectedEmployeeFilter) {
                          setTimeout(() => setShowMobileFilters(false), 300);
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={4} md={6}>
                  <Form.Label>Branch</Form.Label>
                  <Select
                    value={selectedBranchFilter}
                    onChange={(val) => {
                      setSelectedBranchFilter(val);
                      if (fromDate && toDate && val && selectedEmployeeFilter) {
                        setTimeout(() => setShowMobileFilters(false), 300);
                      }
                    }}
                    options={branchOptions}
                    isClearable
                    placeholder="Filter by Branch Name"
                    className="filter-dropdown"
                  />
                </Col>
                <Col xs={12} lg={4} md={6}>
                  <Form.Label>Employee</Form.Label>
                  <Select
                    value={selectedEmployeeFilter}
                    onChange={(val) => {
                      setSelectedEmployeeFilter(val);
                      if (fromDate && toDate && selectedBranchFilter && val) {
                        setTimeout(() => setShowMobileFilters(false), 300);
                      }
                    }}
                    options={employeeOptions}
                    isClearable
                    placeholder="Filter by Employee Name"
                    className="filter-dropdown"
                  />
                </Col>
              </Row>
            </div>
          </div>

          <div className="master-list-section">
            <div className="master-table-wrapper" style={{ padding: "0 15px" }}>
              <DataTable
                columns={columns}
                data={paginationData}
                reportName="ProcessingFeeSummaryReport"
                excelData={filteredData}
              />
            </div>

            <div className="master-pagination-wrapper">
              <CustomPagination
                totalPages={totalPages}
                currentPage={currentPage}
                paginate={paginate}
                totalItems={filteredData.length}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessingFeeSummaryReport;
